import React from "react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  History,
  RotateCcw,
  Trash2,
  Clock,
  FileCode,
  ExternalLink,
  Rocket,
  Loader2,
} from "lucide-react";
import { ProjectSnapshot } from "@/hooks/useProjectSnapshots";
import { formatDistanceToNow, format } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface VersionHistoryPanelProps {
  snapshots: ProjectSnapshot[];
  isLoading: boolean;
  onRestore: (snapshot: ProjectSnapshot) => Promise<void>;
  onDelete: (snapshotId: string) => Promise<boolean>;
}

export function VersionHistoryPanel({
  snapshots,
  isLoading,
  onRestore,
  onDelete,
}: VersionHistoryPanelProps) {
  const [restoring, setRestoring] = useState<string | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<ProjectSnapshot | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const handleRestore = async (snapshot: ProjectSnapshot) => {
    setRestoring(snapshot.id);
    try {
      await onRestore(snapshot);
    } finally {
      setRestoring(null);
      setConfirmRestore(null);
    }
  };

  const handleDelete = async (snapshotId: string) => {
    setDeleting(snapshotId);
    try {
      await onDelete(snapshotId);
    } finally {
      setDeleting(null);
    }
  };

  // Filter to show deploy snapshots (have deploy_url) at top, then others
  const deploySnapshots = snapshots.filter((s) => s.deploy_url);
  const otherSnapshots = snapshots.filter((s) => !s.deploy_url);
  const sortedSnapshots = [...deploySnapshots, ...otherSnapshots];

  return (
    <div
      className="flex flex-col h-full"
      style={{
        background: "#080810",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 px-4 py-3 border-b"
        style={{ borderColor: "rgba(124, 106, 240, 0.15)" }}
      >
        <History className="h-4 w-4" style={{ color: "#6366f1" }} />
        <span
          className="text-sm font-medium"
          style={{ color: "rgba(255, 255, 255, 0.9)" }}
        >
          Version History
        </span>
        <span
          className="ml-auto text-xs px-2 py-0.5 rounded-full"
          style={{
            background: "rgba(99, 102, 241, 0.15)",
            color: "#6366f1",
          }}
        >
          {snapshots.length} versions
        </span>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2
              className="h-6 w-6 animate-spin"
              style={{ color: "#6366f1" }}
            />
          </div>
        ) : snapshots.length === 0 ? (
          <div className="text-center py-12 px-4">
            <History
              className="h-12 w-12 mx-auto mb-4"
              style={{ color: "rgba(99, 102, 241, 0.3)" }}
            />
            <p
              className="text-sm"
              style={{ color: "rgba(255, 255, 255, 0.6)" }}
            >
              No version history yet
            </p>
            <p
              className="text-xs mt-2"
              style={{ color: "rgba(255, 255, 255, 0.4)" }}
            >
              Versions are saved automatically after each build and deploy
            </p>
          </div>
        ) : (
          <div className="p-3 space-y-2">
            {sortedSnapshots.map((snapshot, index) => {
              const isLatest = index === 0;
              const isDeploy = !!snapshot.deploy_url;
              const fileCount =
                snapshot.file_count ?? snapshot.files_snapshot?.length ?? 0;

              return (
                <div
                  key={snapshot.id}
                  className="p-3 rounded-lg border transition-all hover:border-opacity-50"
                  style={{
                    background: isDeploy
                      ? "rgba(99, 102, 241, 0.08)"
                      : "rgba(255, 255, 255, 0.02)",
                    borderColor: isDeploy
                      ? "rgba(99, 102, 241, 0.3)"
                      : "rgba(124, 106, 240, 0.15)",
                  }}
                >
                  {/* Top row: badges and actions */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {isDeploy && (
                        <span
                          className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
                          style={{
                            background: "rgba(34, 197, 94, 0.15)",
                            color: "#22c55e",
                          }}
                        >
                          <Rocket className="h-3 w-3" />
                          Deployed
                        </span>
                      )}
                      {isLatest && (
                        <span
                          className="px-2 py-0.5 rounded-full text-[10px] font-medium"
                          style={{
                            background: "rgba(99, 102, 241, 0.15)",
                            color: "#6366f1",
                          }}
                        >
                          Latest
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1">
                      {isDeploy && snapshot.deploy_url && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          style={{ color: "rgba(255, 255, 255, 0.6)" }}
                          onClick={() =>
                            window.open(snapshot.deploy_url!, "_blank")
                          }
                          title="Open deployed app"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        style={{ color: "rgba(255, 255, 255, 0.6)" }}
                        disabled={restoring === snapshot.id || isLatest}
                        onClick={() => setConfirmRestore(snapshot)}
                        title={isLatest ? "This is the current version" : "Restore this version"}
                      >
                        {restoring === snapshot.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RotateCcw className="h-3.5 w-3.5" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 hover:text-red-400"
                        style={{ color: "rgba(255, 255, 255, 0.4)" }}
                        disabled={deleting === snapshot.id}
                        onClick={() => handleDelete(snapshot.id)}
                        title="Delete this version"
                      >
                        {deleting === snapshot.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Label/prompt */}
                  <p
                    className="text-sm mb-2 line-clamp-2"
                    style={{ color: "rgba(255, 255, 255, 0.85)" }}
                  >
                    {snapshot.label || snapshot.prompt || "Snapshot"}
                  </p>

                  {/* Metadata row */}
                  <div className="flex items-center gap-3 text-xs">
                    <div
                      className="flex items-center gap-1"
                      style={{ color: "rgba(255, 255, 255, 0.5)" }}
                    >
                      <Clock className="h-3 w-3" />
                      <span title={format(new Date(snapshot.created_at), "PPpp")}>
                        {formatDistanceToNow(new Date(snapshot.created_at), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>
                    <div
                      className="flex items-center gap-1"
                      style={{ color: "rgba(255, 255, 255, 0.5)" }}
                    >
                      <FileCode className="h-3 w-3" />
                      <span>{fileCount} files</span>
                    </div>
                  </div>

                  {/* Deploy URL preview */}
                  {isDeploy && snapshot.deploy_url && (
                    <div
                      className="mt-2 px-2 py-1.5 rounded text-xs truncate"
                      style={{
                        background: "rgba(0, 0, 0, 0.3)",
                        color: "rgba(99, 102, 241, 0.9)",
                        fontFamily: "'JetBrains Mono', monospace",
                      }}
                    >
                      {snapshot.deploy_url}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>

      {/* Restore confirmation dialog */}
      <AlertDialog
        open={!!confirmRestore}
        onOpenChange={() => setConfirmRestore(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore to this version?</AlertDialogTitle>
            <AlertDialogDescription>
              This will replace your current app with the version from{" "}
              <strong>
                {confirmRestore
                  ? formatDistanceToNow(new Date(confirmRestore.created_at), {
                      addSuffix: true,
                    })
                  : ""}
              </strong>
              {confirmRestore?.label && ` (${confirmRestore.label})`}. Your
              current version will be saved as a snapshot before restoring.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmRestore && handleRestore(confirmRestore)}
              disabled={!!restoring}
            >
              {restoring ? "Restoring..." : "Restore"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
